/**
 * `FilterBar` — barre de filtres du `DataTable` (plan v3, phase 6).
 *
 * Une facette = une liste déroulante, quelle que soit sa cardinalité. Le rendu
 * en segments d'avant élargissait la barre à proportion du nombre de valeurs :
 * deux facettes de quatre valeurs suffisaient à la faire passer sur deux
 * lignes, et deux tables voisines n'avaient plus la même hauteur d'en-tête. Une
 * pill occupe la même place partout, donc rien à arbitrer par écran.
 *
 * Les compteurs viennent des facettes TanStack (`getFacetedUniqueValues`), qui
 * excluent volontairement le filtre de leur propre colonne : « Active (12) »
 * reste vrai pendant qu'on est en train de regarder « Brouillon ».
 *
 * Aucune dépendance nouvelle : `Input`, `Popover`, `ScrollArea`, `Checkbox`,
 * `Button` et `Badge` existent déjà (pas de `cmdk`).
 */
import * as React from "react";
import type { RowData } from "@tanstack/react-table";
import { ChevronDown, Search, X } from "lucide-react";
import { cn, enTexte } from "@repo/utils";
import { Badge } from "./badge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea } from "./scroll-area";
import type { TableDataTable } from "./data-table-features";

/** Au-delà, la pill affiche son champ de recherche d'options. */
const SEUIL_RECHERCHE_OPTIONS = 8;

export interface FacetteFiltre {
  /** `id` de la colonne à filtrer. Elle doit porter un `filterFn` multi-valeur. */
  colonne: string;
  libelle: string;
  /** Libellés lisibles par valeur brute. Défaut : la valeur elle-même. */
  libelles?: Record<string, string>;
  /** Ordre d'affichage des valeurs. Défaut : ordre alphabétique des libellés. */
  ordre?: readonly string[];
}

export interface ConfigBarreFiltres {
  /** Recherche texte, portée par une colonne à `filterFn` de recherche. */
  recherche?: { colonne: string; placeholder?: string; libelle?: string };
  facettes?: FacetteFiltre[];
}

interface Props<T extends RowData> extends ConfigBarreFiltres {
  table: TableDataTable<T>;
}

/** Valeur de filtre d'une facette, toujours ramenée à une liste. */
function selection(valeur: unknown): string[] {
  if (Array.isArray(valeur)) return (valeur as unknown[]).map(enTexte);
  if (valeur === undefined || valeur === null || valeur === "") return [];
  return [enTexte(valeur)];
}

export function FilterBar<T extends RowData>({
  table,
  recherche,
  facettes,
}: Props<T>) {
  const colonneRecherche = recherche
    ? table.getColumn(recherche.colonne)
    : undefined;
  const requete = enTexte(colonneRecherche?.getFilterValue());

  const colonnesFacettes = (facettes ?? []).flatMap((facette) => {
    const colonne = table.getColumn(facette.colonne);
    return colonne ? [{ facette, colonne }] : [];
  });

  const aFiltre =
    requete !== "" || colonnesFacettes.some(({ colonne }) => selection(colonne.getFilterValue()).length > 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {colonneRecherche && (
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            // Champ de recherche en pastille, comme les pills de facette qui le
            // suivent. Les champs de formulaire, eux, gardent leur rayon.
            className="rounded-full pl-9"
            aria-label={recherche?.libelle ?? recherche?.placeholder ?? "Rechercher"}
            placeholder={recherche?.placeholder ?? "Rechercher…"}
            // Pas de debounce : le filtrage est client, il n'y a rien à
            // ménager. Le debounce de la v2 protégeait un aller-retour serveur
            // qui n'existe plus.
            value={requete}
            onChange={(e) => colonneRecherche.setFilterValue(e.target.value)}
          />
        </div>
      )}

      {colonnesFacettes.map(({ facette, colonne }) => {
        const comptes = colonne.getFacetedUniqueValues();
        const valeurs = ordonner(comptes, facette);
        if (valeurs.length === 0) return null;
        const active = selection(colonne.getFilterValue());
        const basculer = (valeur: string) => {
          const suivante = active.includes(valeur)
            ? active.filter((v) => v !== valeur)
            : [...active, valeur];
          colonne.setFilterValue(suivante.length > 0 ? suivante : undefined);
        };

        return (
          <PillFacette
            key={facette.colonne}
            facette={facette}
            valeurs={valeurs}
            comptes={comptes}
            active={active}
            onBasculer={basculer}
            onEffacer={() => colonne.setFilterValue(undefined)}
          />
        );
      })}

      {aFiltre && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => table.resetColumnFilters()}
        >
          <X className="h-4 w-4 mr-1" />
          Réinitialiser
        </Button>
      )}
    </div>
  );
}

/** Valeurs de la facette, dans l'ordre imposé sinon par libellé. */
function ordonner(
  comptes: Map<unknown, number>,
  facette: FacetteFiltre,
): string[] {
  const presentes = [...comptes.keys()]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .map(String);
  if (facette.ordre) {
    return facette.ordre.filter((v) => presentes.includes(v));
  }
  const libelle = (v: string) => facette.libelles?.[v] ?? v;
  return presentes.sort((a, b) => libelle(a).localeCompare(libelle(b), "fr"));
}

interface PropsControle {
  facette: FacetteFiltre;
  valeurs: string[];
  comptes: Map<unknown, number>;
  active: string[];
  onBasculer: (valeur: string) => void;
  onEffacer: () => void;
}

/** Une pill par facette : liste cherchable au-delà de quelques options. */
function PillFacette({
  facette,
  valeurs,
  comptes,
  active,
  onBasculer,
  onEffacer,
}: PropsControle) {
  const [requeteOptions, setRequeteOptions] = React.useState("");
  /** Sélection unique : lisible sur le déclencheur, sans ouvrir la liste. */
  const seule = active.length === 1 ? active[0] : undefined;
  const avecRecherche = valeurs.length > SEUIL_RECHERCHE_OPTIONS;
  const filtrees = avecRecherche
    ? valeurs.filter((v) =>
        (facette.libelles?.[v] ?? v)
          .toLowerCase()
          .includes(requeteOptions.toLowerCase()),
      )
    : valeurs;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          // Le déclencheur porte l'état du filtre : une sélection unique se lit
          // sans ouvrir la liste, au-delà c'est un compte (les afficher toutes
          // ferait varier la largeur de la barre à chaque clic).
          aria-label={`Filtrer par ${facette.libelle}`}
          className={cn(
            active.length > 0 &&
              "border-charte-line-accent-strong bg-charte-wash-accent text-accent-foreground hover:bg-charte-wash-accent-strong",
          )}
        >
          {facette.libelle}
          {seule !== undefined && (
            <span className="ml-1.5 max-w-32 truncate font-normal">
              : {facette.libelles?.[seule] ?? seule}
            </span>
          )}
          {active.length > 1 && (
            <Badge variant="secondary" className="ml-2 data">
              {active.length}
            </Badge>
          )}
          <ChevronDown className="h-4 w-4 ml-1.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        {avecRecherche && (
          <Input
            className="mb-2 h-8"
            aria-label={`Chercher dans ${facette.libelle}`}
            placeholder="Chercher…"
            value={requeteOptions}
            onChange={(e) => setRequeteOptions(e.target.value)}
          />
        )}
        <ScrollArea className={cn(filtrees.length > 8 && "h-64")}>
          {filtrees.length === 0 && (
            <p className="text-sm text-muted-foreground px-1 py-2">
              Aucune valeur
            </p>
          )}
          {filtrees.map((valeur) => (
            <div
              key={valeur}
              className="flex items-center gap-2 rounded-full px-2 py-1 transition-colors duration-150 hover:bg-charte-hover"
            >
              <Checkbox
                id={`facette-${facette.colonne}-${valeur}`}
                checked={active.includes(valeur)}
                onCheckedChange={() => onBasculer(valeur)}
              />
              <label
                htmlFor={`facette-${facette.colonne}-${valeur}`}
                className="text-sm cursor-pointer flex-1 truncate"
              >
                {facette.libelles?.[valeur] ?? valeur}
              </label>
              <span className="data text-xs text-muted-foreground">
                {comptes.get(valeur) ?? 0}
              </span>
            </div>
          ))}
        </ScrollArea>
        {active.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1"
            onClick={onEffacer}
          >
            Effacer
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
